import type { Video } from "@/types/video";

export interface CategoryMeta {
  key: string;
  name: string;
  label: string;
  desc: string;
  icon: string;
}

export const categories: CategoryMeta[] = [
  {
    key: "movie",
    name: "电影",
    label: "movie",
    desc: "院线大片与口碑佳作，科幻、动作、悬疑、爱情一网打尽。",
    icon: "ri-film-line",
  },
  {
    key: "tv",
    name: "电视剧",
    label: "tv",
    desc: "国产剧、韩剧、日剧、英剧持续更新，追剧不迷路。",
    icon: "ri-tv-2-line",
  },
  {
    key: "variety",
    name: "综艺",
    label: "variety",
    desc: "真人秀、脱口秀、音乐选秀，轻松一刻笑料不断。",
    icon: "ri-mic-line",
  },
];

export function getCategory(key?: string): CategoryMeta | undefined {
  return categories.find((c) => c.key === key);
}

export function typeToKey(type: string): string {
  const found = categories.find((c) => c.name === type);
  return found ? found.key : "movie";
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return `${views}`;
}

export function formatDate(date: string): string {
  return date.replace(/-/g, " / ");
}

export function getVideoById(list: Video[], id: number): Video | undefined {
  return list.find((v) => v.id === id);
}

export function searchVideos(list: Video[], keyword: string): Video[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return list.filter(
    (v) =>
      v.title.toLowerCase().includes(q) ||
      v.type.toLowerCase().includes(q) ||
      v.region.toLowerCase().includes(q) ||
      v.cast.toLowerCase().includes(q) ||
      v.tags.some((t) => t.toLowerCase().includes(q)),
  );
}

export function getRelated(list: Video[], current: Video, count = 6): Video[] {
  const scored = list
    .filter((v) => v.id !== current.id)
    .map((v) => {
      let score = 0;
      if (v.typeKey === current.typeKey) score += 3;
      if (v.region === current.region) score += 1;
      score += v.tags.filter((t) => current.tags.includes(t)).length * 2;
      return { v, score };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((s) => s.v);
}

export function getHotRanking(list: Video[], count = 10): Video[] {
  return [...list].sort((a, b) => b.views - a.views).slice(0, count);
}

export function getLatest(list: Video[], count?: number): Video[] {
  const sorted = [...list].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
  return typeof count === "number" ? sorted.slice(0, count) : sorted;
}

export function getFeatured(list: Video[]): Video[] {
  const featured = list.filter((v) => v.featured);
  return featured.length >= 4 ? featured : list.slice(0, 4);
}

export function highlight(text: string, keyword: string): { part: string; hit: boolean }[] {
  const q = keyword.trim();
  if (!q) return [{ part: text, hit: false }];
  const lower = text.toLowerCase();
  const target = q.toLowerCase();
  const result: { part: string; hit: boolean }[] = [];
  let index = 0;
  while (index < text.length) {
    const found = lower.indexOf(target, index);
    if (found === -1) {
      result.push({ part: text.slice(index), hit: false });
      break;
    }
    if (found > index) {
      result.push({ part: text.slice(index, found), hit: false });
    }
    result.push({ part: text.slice(found, found + q.length), hit: true });
    index = found + q.length;
  }
  return result;
}